import { useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import { useChartContext } from '../ChartProvider'

function cleanChartConfig (config) {
  Object.keys(config).forEach((key) => {
    if (typeof config[key] === 'undefined') {
      delete config[key]
    }
  })

  return config
}

function PieChart ({
  seriesId,
  name,
  colorBy,
  legendHoverLink,
  coordinateSystem,
  geoIndex,
  calendarIndex,
  selectedMode,
  selectedOffset,
  clockwise,
  startAngle,
  endAngle,
  minAngle,
  padAngle,
  minShowLabelAngle,
  roseType,
  avoidLabelOverlap,
  stillShowZeroSum,
  percentPrecision,
  cursor,
  zlevel,
  z,
  left,
  top,
  right,
  bottom,
  width,
  height,
  showEmptyCircle,
  emptyCircleStyle,
  label,
  labelLine,
  labelLayout,
  itemStyle,
  emphasis,
  blur,
  select,
  center,
  radius,
  seriesLayoutBy,
  datasetIndex,
  dimensions,
  encode,
  dataGroupId,
  data,
  markPoint,
  markLine,
  markArea,
  silent,
  animationType,
  animationTypeUpdate,
  animation,
  animationThreshold,
  animationDuration,
  animationEasing,
  animationDelay,
  animationDurationUpdate,
  animationEasingUpdate,
  animationDelayUpdate,
  universalTransition,
  tooltip
}) {
  const { setSeries } = useChartContext()

  useLayoutEffect(() => {
    setSeries(seriesId, cleanChartConfig({
      type: 'pie',
      id: seriesId,
      name,
      colorBy,
      legendHoverLink,
      coordinateSystem,
      geoIndex,
      calendarIndex,
      selectedMode,
      selectedOffset,
      clockwise,
      startAngle,
      endAngle,
      minAngle,
      padAngle,
      minShowLabelAngle,
      roseType,
      avoidLabelOverlap,
      stillShowZeroSum,
      percentPrecision,
      cursor,
      zlevel,
      z,
      left,
      top,
      right,
      bottom,
      width,
      height,
      showEmptyCircle,
      emptyCircleStyle,
      label,
      labelLine,
      labelLayout,
      itemStyle,
      emphasis,
      blur,
      select,
      center,
      radius,
      seriesLayoutBy,
      datasetIndex,
      dimensions,
      encode,
      dataGroupId,
      data,
      markPoint,
      markLine,
      markArea,
      silent,
      animationType,
      animationTypeUpdate,
      animation,
      animationThreshold,
      animationDuration,
      animationEasing,
      animationDelay,
      animationDurationUpdate,
      animationEasingUpdate,
      animationDelayUpdate,
      universalTransition,
      tooltip
    }))
  }, [
    setSeries,
    seriesId,
    name,
    colorBy,
    legendHoverLink,
    coordinateSystem,
    geoIndex,
    calendarIndex,
    selectedMode,
    selectedOffset,
    clockwise,
    startAngle,
    endAngle,
    minAngle,
    padAngle,
    minShowLabelAngle,
    roseType,
    avoidLabelOverlap,
    stillShowZeroSum,
    percentPrecision,
    cursor,
    zlevel,
    z,
    left,
    top,
    right,
    bottom,
    width,
    height,
    showEmptyCircle,
    emptyCircleStyle,
    label,
    labelLine,
    labelLayout,
    itemStyle,
    emphasis,
    blur,
    select,
    center,
    radius,
    seriesLayoutBy,
    datasetIndex,
    dimensions,
    encode,
    dataGroupId,
    data,
    markPoint,
    markLine,
    markArea,
    silent,
    animationType,
    animationTypeUpdate,
    animation,
    animationThreshold,
    animationDuration,
    animationEasing,
    animationDelay,
    animationDurationUpdate,
    animationEasingUpdate,
    animationDelayUpdate,
    universalTransition,
    tooltip
  ])

  return null
}

PieChart.propTypes = {
  seriesId: PropTypes.string,
  /** Series name used for displaying in tooltip and filtering with legend, or updating data and configuration with setOption. **/
  name: PropTypes.string,
  /** The policy to take color from option.color. Valid values:

   'series': assigns the colors in the palette by series, so that all data in the same series are in the same color;
   'data': assigns colors in the palette according to data items, with each data item using a different color. **/
  colorBy: PropTypes.oneOf(['data', 'series']),
  /** Whether to enable highlighting chart when legend is being hovered. **/
  legendHoverLink: PropTypes.bool,
  /** The coordinate used in the series, whose options are:

   null or 'none'

   No coordinate.

   'geo'

   Use geographic coordinate, with geoIndex to assign the corresponding geographic coordinate components.

   'calendar'

   Use calendar coordinates, with calendarIndex to assign the corresponding calendar coordinate components.

   'none'

   Do not use coordinate system. **/
  coordinateSystem: PropTypes.oneOf([null, 'none', 'geo', 'calendar']),
  /** Index of geographic coordinate to combine with, which is useful for multiple geographic axes in one chart. **/
  geoIndex: PropTypes.number,
  /** Index of calendar coordinates to combine with, which is useful for multiple calendar coordinates in one chart. **/
  calendarIndex: PropTypes.number,
  /** Selected mode. It is disabled by default, and you may set it to be true to enable it.

   Besides, it can be set to 'single', 'multiple' or 'series', for single selection, multiple selections and whole series selection. **/
  selectedMode: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /** The offset distance of selected sector. **/
  selectedOffset: PropTypes.number,
  /** Whether the layout of sectors of pie chart is clockwise. **/
  clockwise: PropTypes.bool,
  /** The start angle, which range is [0, 360]. **/
  startAngle: PropTypes.number,
  /** The end angle, the default value is 'auto'.

   When the value is 'auto', the end angle is calculated automatically based on startAngle to ensure it is a complete circle. **/
  endAngle: PropTypes.number,
  /** The minimum angle of sector (0 ~ 360). It prevents some sector from being too small when value is small, which will affect user interaction. **/
  minAngle: PropTypes.number,
  /** The interval angle between the sectors (0 ~ 360). **/
  padAngle: PropTypes.number,
  /** If a sector is less than this angle (0 ~ 360), label and labelLine will not be displayed. **/
  minShowLabelAngle: PropTypes.number,
  /** Whether to show as Nightingale chart, which distinguishs data through radius. There are 2 optional modes:

   'radius' Use central angle to show the percentage of data, radius to show data size.
   'area' All the sectors will share the same central angle, the data size is shown only through radiuses. **/
  roseType: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** Whether to enable the strategy to avoid labels overlap. Defaults to be enabled, which will move the label positions in the case of labels overlapping **/
  avoidLabelOverlap: PropTypes.bool,
  /** Whether to show sector when all data are zero. **/
  stillShowZeroSum: PropTypes.bool,

  percentPrecision: PropTypes.number,
  cursor: PropTypes.string,
  zlevel: PropTypes.number,
  z: PropTypes.number,
  left: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  top: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  right: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showEmptyCircle: PropTypes.bool,
  emptyCircleStyle: PropTypes.object,
  label: PropTypes.object,
  labelLine: PropTypes.object,
  labelLayout: PropTypes.object,
  itemStyle: PropTypes.object,
  emphasis: PropTypes.object,
  blur: PropTypes.object,
  select: PropTypes.object,
  center: PropTypes.array,
  radius: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  seriesLayoutBy: PropTypes.oneOf(['column', 'row']),
  datasetIndex: PropTypes.number,
  dimensions: PropTypes.array,
  encode: PropTypes.object,
  dataGroupId: PropTypes.string,
  data: PropTypes.array,
  markPoint: PropTypes.object,
  markLine: PropTypes.object,
  markArea: PropTypes.object,
  silent: PropTypes.bool,
  animationType: PropTypes.oneOf(['scale', 'expansion']),
  animationTypeUpdate: PropTypes.oneOf(['transition', 'expansion']),
  animation: PropTypes.bool,
  animationThreshold: PropTypes.number,
  animationDuration: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  animationEasing: PropTypes.string,
  animationDelay: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  animationDurationUpdate: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  animationEasingUpdate: PropTypes.string,
  animationDelayUpdate: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  universalTransition: PropTypes.object,
  tooltip: PropTypes.object
}

PieChart.defaultProps = {
  // radius: ['50%', '50%'],
  colorBy: 'data'
  // minAngle: 90,
  // maxAngle: -270
}

export default PieChart
